'use client';

import * as React from 'react';

import { cn } from '@repo/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger, useTapTooltip } from './tooltip';

interface HintRow {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Renders above the rule as a summed or concluding line. */
  total?: boolean;
}

interface InfoLabelProps {
  children: React.ReactNode;
  /** Heading inside the tooltip. Defaults to the label itself. */
  hintTitle?: React.ReactNode;
  /** The definition, in a sentence or two. */
  hint?: React.ReactNode;
  /** Optional breakdown under the definition — the numbers behind it. */
  hintRows?: HintRow[];
  className?: string;
}

/**
 * A label that explains itself on demand.
 *
 * The affordance is a dotted underline rather than an info icon: it marks
 * the term itself as the thing with a definition, costs no horizontal
 * space beside the label, and does not put a row of icons down a column of
 * metrics. Hovering a word is also the gesture people already have for
 * "what does this mean".
 *
 * For a professional audience the definition belongs behind that gesture,
 * not printed under every row — a reader who knows what tracking error is
 * should not scroll past the definition on every visit to spare someone
 * else one hover.
 */
function InfoLabel({ children, hintTitle, hint, hintRows, className }: InfoLabelProps) {
  const tap = useTapTooltip();
  if (!hint && !hintRows?.length) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Tooltip {...tap.rootProps}>
      <TooltipTrigger asChild {...tap.triggerProps}>
        <span
          // Reference, not an action: reachable by keyboard, never
          // announced as a control that changes something.
          tabIndex={0}
          role="note"
          className={cn(
            'decoration-rowa-text-tertiary/60 hover:decoration-rowa-text-secondary focus-visible:ring-rowa-border-accent cursor-help rounded-sm underline decoration-dotted underline-offset-4 focus-visible:ring-2 focus-visible:outline-none',
            className
          )}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className={HINT_CONTENT_CLASS_NAME}>
        <HintContent title={hintTitle ?? children} hint={hint} rows={hintRows} />
      </TooltipContent>
    </Tooltip>
  );
}

/** The tooltip panel's own size: room for a definition and its rows. */
const HINT_CONTENT_CLASS_NAME = 'w-72 max-w-[min(18rem,calc(100vw-2rem))] p-4';

/**
 * The inside of a definition tooltip: a heading, the definition, then the
 * numbers behind it. Shared by the hoverable label and the hoverable
 * metric card, so both explain themselves in the same shape.
 */
function HintContent({
  title,
  hint,
  rows = []
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  rows?: HintRow[];
}) {
  const totals = rows.filter((row) => row.total);
  const details = rows.filter((row) => !row.total);

  return (
    <>
      <p className="text-rowa-label-md text-rowa-text-primary font-semibold">{title}</p>
      {hint ? (
        <div className="text-rowa-body-sm text-rowa-text-secondary mt-1.5 text-balance">{hint}</div>
      ) : null}

      {details.length > 0 ? (
        <dl className="bg-rowa-surface-inset rounded-rowa-md mt-3 space-y-1.5 p-3">
          {details.map((row, index) => (
            <div key={index} className="flex items-baseline justify-between gap-3">
              <dt className="text-rowa-body-sm text-rowa-text-secondary">{row.label}</dt>
              <dd className="text-rowa-body-sm text-rowa-text-primary text-right font-medium tabular-nums">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {totals.map((row, index) => (
        <div
          key={index}
          className="border-rowa-border-subtle mt-3 flex items-baseline justify-between gap-3 border-t pt-3"
        >
          <span className="text-rowa-body-sm text-rowa-text-primary">{row.label}</span>
          <span className="text-rowa-body-sm text-rowa-text-primary font-semibold tabular-nums">
            {row.value}
          </span>
        </div>
      ))}
    </>
  );
}

export { InfoLabel, HintContent, HINT_CONTENT_CLASS_NAME };
export type { InfoLabelProps, HintRow };
